import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { requestWrapper, roundFloat } from 'Shared/utils'

import { STORE_UI, UNREAD } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces'
import { getDefaultStoreArrayValue, getDefaultStoreValue } from 'Core/utils'
import { convertDateFromStringToMoment } from 'Core/utils/date'

import {
	Permission,
	STORE_AUTH,
	STORE_DOCUMENT,
	STORE_SITE_VISIT
} from 'Portals/Supplier/constants'
import { Dashboard } from 'Portals/Supplier/entities'
import { DashboardRepo } from 'Portals/Supplier/repositories'
import { RootStore } from 'Portals/Supplier/stores'

export class DashboardStore {
	rootStore: RootStore
	@observable accessor landfillStatistics = getDefaultStoreValue<Dashboard.LandfillStatistics>()
	@observable accessor treatmentMethodsMostUsed =
		getDefaultStoreArrayValue<Dashboard.TreatmentMethod.MostUsed>()
	@observable accessor landfills = getDefaultStoreArrayValue<Dashboard.Landfill>()
	@observable accessor lastReports =
		getDefaultStoreValue<{ reports: Dashboard.Report.Basic[]; total: number }>()
	@observable accessor mostUsedItems =
		getDefaultStoreValue<{ items: Dashboard.Item.MostUsedItem[]; total: number }>()
	@observable accessor notifications = getDefaultStoreArrayValue<Dashboard.Notification>()
	@observable accessor lastThreeInvoices = getDefaultStoreArrayValue<Dashboard.LastThreeBasic>()

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore
	}

	@flow.bound
	*retrieveLandfillStatistics(): CustomGenerator<Dashboard.LandfillStatistics> {
		yield requestWrapper({
			field: this.landfillStatistics,
			repository: DashboardRepo.retrieveLandfillStatistics,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveTreatmentMethodsMostUsed(): CustomGenerator<Dashboard.TreatmentMethod.MostUsed[]> {
		yield requestWrapper({
			field: this.treatmentMethodsMostUsed,
			repository: DashboardRepo.retrieveTreatmentMethodsMostUsed,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveLastThreeInvoices(): CustomGenerator<Dashboard.LastThreeBasic[]> {
		yield requestWrapper({
			field: this.lastThreeInvoices,
			repository: DashboardRepo.retrieveLastThreeInvoices,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveDashboardWithDetails() {
		const hasReportingViewPermission = this.rootStore[STORE_AUTH].isPermissionsExist([
			Permission.REPORTING_VIEW
		])

		try {
			yield Promise.all([
				this.retrieveTreatmentMethodsMostUsed(),
				this.retrieveLandfillStatistics(),
				this.retrieveLandfills(),
				this.retrieveMostUsedItems(),
				this.retrieveLastThreeInvoices(),
				this.retrieveNotifications(),
				this.rootStore[STORE_DOCUMENT].retrieveDocumentTypes(),
				this.rootStore[STORE_DOCUMENT].retrieveStatuses(),
				this.rootStore[STORE_SITE_VISIT].retrieveAvailableSupplierLocations(),
				hasReportingViewPermission && this.retrieveLastReports()
			])
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification()
		}
	}

	@flow.bound
	*retrieveLandfills(): CustomGenerator<Dashboard.Landfill[]> {
		yield requestWrapper({
			field: this.landfills,
			repository: DashboardRepo.retrieveLandfills,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@computed
	get formattedTreatmentMethodsMostUsed() {
		return this.treatmentMethodsMostUsed.value.map((item) => ({
			value: item.percent,
			label: `${item.name} (${roundFloat(item.percent)}%)`
		}))
	}

	@computed
	get formattedLandfillStatistics() {
		const { value } = this.landfillStatistics
		return {
			nonLandfill: value?.nonLandfill || 0,
			landfill: value?.landfill || 0,
			nonLandfillPercent: value?.nonLandfillPercent || 0,
			landfillPercent: value?.landfillPercent || 0,
			total: value?.totalTons || 0,
			totalSpent: null,
			totalEarning: value?.total || 0,
			currencyCode: value?.currencyCode
		}
	}

	@computed
	get formattedLandfills() {
		return this.landfills.value.map((item) => ({
			label: convertDateFromStringToMoment(item.month)?.format('MMMM YYYY') || '',
			value1: item.landfillPercent,
			value2: item.nonLandfillPercent
		}))
	}

	@flow.bound
	*retrieveLastReports(): CustomGenerator<{ reports: Dashboard.Report.Basic[]; total: number }> {
		yield requestWrapper({
			field: this.lastReports,
			repository: DashboardRepo.retrieveLastReports,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveMostUsedItems(): CustomGenerator<{
		items: Dashboard.Item.MostUsedItem[]
		total: number
	}> {
		yield requestWrapper({
			field: this.mostUsedItems,
			repository: DashboardRepo.retrieveMostUsedItems,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveNotifications(): CustomGenerator<Dashboard.Notification[]> {
		yield requestWrapper({
			field: this.notifications,
			repository: DashboardRepo.retrieveNotifications,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow.bound
	*markAsRead(notificationIds: Dashboard.NotificationIds) {
		this.rootStore[STORE_UI].setIsRequestSubmitting()
		try {
			yield DashboardRepo.markAsRead(notificationIds)
			this.retrieveNotifications()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@computed
	get notificationIds(): Dashboard.NotificationIds {
		return {
			notificationIds: this.notifications?.value?.map((notification) => notification.id) || []
		}
	}

	@action.bound
	getFilteredNotifications(filter: string) {
		return this.notifications.value?.filter((notification) => {
			if (filter === UNREAD) {
				return !notification.isRead
			}
			return true
		})
	}

	applyAdditionalCallback: (() => void) | undefined = () => {
		this.markAsRead(this.notificationIds)
		this.retrieveNotifications()
	}

	@computed
	get hasUnreadNotifications(): boolean {
		return this.notifications.value.some((notification) => !notification.isRead)
	}
}
